/**
 * Renders reference/catalog text (drug name, lab test name, master-data label, etc.) with a
 * two-tier translation fallback:
 *   1. A human-curated translation from entity_translations, if one has been entered for this
 *      record (passed in via `manual`) — always preferred, since it's verified.
 *   2. Otherwise, an on-the-fly LibreTranslate translation via the cached useAutoTranslate hook
 *      (backend POST /translate/auto) — shows the original text immediately, then swaps in the
 *      machine translation once it resolves (instantly on repeat views, since it's cached
 *      server-side after the first time anyone sees this exact string in this language).
 *
 * Deliberately a small standalone component (not a plain helper function) so it can call the
 * useAutoTranslate hook safely from inside a list .map() without violating the rules of hooks —
 * each rendered row gets its own component instance/hook call.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoTranslate } from '../../utils/autoTranslate';

interface Props {
  text: string | undefined | null;
  manual?: string; // pre-resolved human-curated translation for the current language, if any
}

export default function AutoTranslatedText({ text, manual }: Props) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  // Skip the network round-trip entirely once a verified manual translation is available.
  const auto = useAutoTranslate(manual ? '' : text);
  if (lang === 'en' || !text) return <>{text || ''}</>;
  return <>{manual || auto || text}</>;
}
